<template>
  <div>
    <Dialog
      :header="$t('reclamacion.dialogTituloAsignarNotario')"
      :modal="true"
      :visible.sync="displayDialogAsignarNotario"
      :contentStyle="{ overflow: 'visible' }"
    >
      <p>{{ $t("reclamacion.dialogAvisoAsignacionNotario") }}</p>
      <template #footer>
        <Button
          :label="$t('common.buttons.confirm')"
          icon="pi pi-check"
          @click="asignarNotario(dataTableSelectedReclamacionBoton.token)"
        />
        <Button
          :label="$t('common.buttons.cancel')"
          icon="pi pi-times"
          class="button-secondary"
          @click="closeAsignarNotarioOnClick"
        />
      </template>
    </Dialog>

    <SpecificFilters
      v-show="this.operacionId == undefined"
      :displayMyOperationsAgentFilter="displayMyOperationsAgentFilter"
      :dateFieldName="FILTER_SEARCH_FECHA_ALTA"
      :displayMyClaimsAgentFilter="displayMyClaimsAgentFilter"
      :key="'SpecificFilters' + switchSpecificFilters"
      @searchBySpecificFilters="filterBySpecificFilters"
    />

    <Filters
      v-show="backendFilters.length > 0"
      :entity="'reclamacion'"
      :computedFilters="backendFilters"
      :literalLists="[statuses, products, phases]"
      :fieldsToExcludeFromFilters="fieldsToExcludeFromFilters"
      @filterToRemove="removeFilter"
      @resetAllFilters="resetFilters(filters, fieldsToExcludeFromFilters)"
    />

    <Toast />

    <DataTable
      :value="dataTableReclamacion"
      :loading="loading"
      :lazy="true"
      :selection.sync="dataTableSelectedReclamacion"
      :paginator="true"
      :rows="tamPagina"
      :totalRecords="totalRecords"
      :filters.sync="filters"
      class="p-datatable-responsive"
      selectionMode="single"
      dataKey="token"
      @page="onPage($event)"
      @sort="customSort($event)"
      @row-click="gotoDetalle($event.data.token)"
      :autoLayout="true"
    >
      <template #header>
        <div class="table-header">
          <span class="title">{{ $t("reclamacion.tituloHeader") }}</span>
        </div>
      </template>

      <template #footer>
        <div class="grid">
          <div class="col-4 col-offset-8" style="text-align: right">
            <ProgressSpinner
              v-if="descargandoCSV"
              strokeWidth="8"
              style="width: 0.5cm; height: 0.5cm; margin-bottom: -4px"
            />
            <Button
              @click="downloadCSV()"
              icon="pi pi-external-link"
              :label="$t('common.buttons.export')"
              class="p-button-text p-button-outlined"
              :disabled="descargandoCSV"
            />
            <ButtonResetFilters
              :filtersApplied="filters"
              :fieldsToExcludeFromFilters="fieldsToExcludeFromFilters"
              v-if="backendFilters.length > 0"
            />
          </div>
        </div>
      </template>

      <template #empty>
        {{ $t("common.noResultados") }}
      </template>

      <Column
        field="token"
        :header="$t('reclamacion.token')"
        headerStyle="width: 7em"
      >
        <template #body="slotProps">
          <span class="column-title">{{ $t("reclamacion.token") }}</span>
          {{ slotProps.data.token }}
        </template>
        <template #filter>
          <InputText
            type="text"
            v-model="filters['token']"
            class="column-filter"
            :placeholder="$t('reclamacion.placeholder.igual_a')"
          />
        </template>
      </Column>

      <Column
        field="fechaAlta"
        :header="$t('reclamacion.listafechaAlta')"
        headerStyle="width: 9em"
        :sortable="true"
      >
        <template #body="slotProps">
          <span class="column-title">{{
            $t("reclamacion.listafechaAlta")
          }}</span>
          {{ getFormattedDateShort(slotProps.data.fechaAlta) }}
        </template>
      </Column>

      <Column field="totalPendiente" :header="$t('reclamacion.totalPendiente')">
        <template #body="slotProps">
          <span class="column-title">{{
            $t("reclamacion.totalPendiente")
          }}</span>
          {{
            slotProps.data.totalPendiente
              | toCurrency("es-ES", slotProps.data.divisa)
          }}
        </template>
      </Column>

      <Column
        field="estado"
        :header="$t('reclamacion.estado')"
        :sortable="true"
        filterMatchMode="contains"
      >
        <template #body="slotProps">
          <span class="column-title">{{ $t("reclamacion.estado") }}</span>
          <span :class="getEstiloEstadoColor(slotProps.data.estado)">{{
            slotProps.data.literalEstado.valor
          }}</span>
        </template>
        <template #filter>
          <Dropdown
            v-model="filters['estado']"
            :options="statuses"
            optionLabel="name"
            optionValue="code"
            :placeholder="$t('reclamacion.placeholder.igual_a_estado')"
            :filter="true"
            class="column-filter estado-dropdown"
            :showClear="true"
          />
        </template>
      </Column>

      <Column
        field="operacion.acreedor.idLegal"
        :header="$t('reclamacion.acreedor')"
      >
        <template #body="slotProps">
          <span class="column-title">{{ $t("reclamacion.acreedor") }}</span>
          {{ slotProps.data.acreedorIdLegal }}
        </template>
        <template #filter>
          <InputText
            type="text"
            v-model="filters['operacion.acreedor.idLegal']"
            class="column-filter"
            :placeholder="$t('reclamacion.placeholder.igual_a')"
          />
        </template>
      </Column>
      <Column
        field="operacion.acreedor.nombre"
        :header="$t('reclamacion.acreedor')"
      >
        <template #body="slotProps">
          <span class="column-title">{{ $t("reclamacion.acreedor") }}</span>
          {{ slotProps.data.acreedorNombre }}
        </template>
        <template #filter>
          <InputText
            type="text"
            v-model="filters['operacion.acreedor.nombre']"
            class="column-filter"
            :placeholder="$t('reclamacion.placeholder.igual_a')"
          />
        </template>
      </Column>
      <Column
        field="operacion.deudor.idLegal"
        :header="$t('reclamacion.deudor')"
      >
        <template #body="slotProps">
          <span class="column-title">{{ $t("reclamacion.deudor") }}</span>
          {{ slotProps.data.deudorIdLegal }}
        </template>
        <template #filter>
          <InputText
            type="text"
            v-model="filters['operacion.deudor.idLegal']"
            class="column-filter"
            :placeholder="$t('reclamacion.placeholder.igual_a')"
          />
        </template>
      </Column>
      <Column
        field="operacion.deudor.nombre"
        :header="$t('reclamacion.deudor')"
      >
        <template #body="slotProps">
          <span class="column-title">{{ $t("reclamacion.idDeudor") }}</span>
          {{ slotProps.data.deudorNombre }}
        </template>
        <template #filter>
          <InputText
            type="text"
            v-model="filters['operacion.deudor.nombre']"
            class="column-filter"
            :placeholder="$t('reclamacion.placeholder.igual_a')"
          />
        </template>
      </Column>

      <Column
        field="operacion.producto.codigoProducto"
        :header="$t('reclamacion.producto')"
      >
        <template #body="slotProps">
          <span class="column-title">{{ $t("reclamacion.producto") }}</span>
          {{ slotProps.data.literalProducto.valor }}
        </template>
      </Column>
    </DataTable>
    <Dialog
      :header="$t('reclamacion.dialogCSVTituloExportarCSV')"
      :modal="true"
      :visible.sync="displayDialogExportarCSV"
      :contentStyle="{ overflow: 'visible' }"
    >
      <p>
        {{
          $t("reclamacion.dialogCSVmensajeExportarCSV", {
            totalRecords: totalRecords,
          })
        }}
      </p>
      <template #footer>
        <Button
          :label="$t('common.buttons.confirm')"
          icon="pi pi-check"
          @click="getReclamaciones()"
        />
        <Button
          :label="$t('common.buttons.cancel')"
          icon="pi pi-times"
          class="button-secondary"
          @click="displayDialogExportarCSV = false"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import RoutesMixin from "@/utils/routes";
import DatesMixin from "@/utils/dates";
import StringsMixin from "@/utils/strings";
import CurrencyMixin from "@/utils/currency";
import JavascriptMixin from "@/utils/javascript";
import ReclamacionMixin from "@/utils/reclamacion";
import ExportCSVMixin from "@/utils/exportCSV";
import AbilitiesMixin from "@/utils/ability";
import FiltersMixin from "@/utils/filters";
import ProductsMixin from "@/utils/productos";
import ProgressSpinner from "primevue/progressspinner";
import Filters from "@/components/utilidades/Filters.vue";
import SpecificFilters from "@/components/utilidades/SpecificFilters.vue";
import ButtonResetFilters from "@/components/utilidades/ButtonResetFilters.vue";
import {
  PRODUCTOS,
  ORIGEN_BOTONERA_RECLAMACIONES,
} from "@/resources/constants";
import {
  QUERY_LISTADO_RECLAMACION,
  EN_NOTARIA_RECLAMACION,
  QUERY_LISTADOS_LITERALES_ESTADOS,
} from "@/graphQLQueries/ReclamacionQueries";

export const FILTER_SEARCH_FECHA_RECLAMACION = "fechaAlta";
export const FILTER_SEARCH_NOTARIO_UUID = "notario.uuid";
export const IS_NULL = "IS_NULL";
export const FILTER_SEARCH_OPERACION_ID = "operacion.id";
export const FILTER_SEARCH_AGENTE_UUID = "operacion.agente.uuid";
export const FILTER_SEARCH_CLIENTE_IDLEGAL =
  "operacion.producto.contrato.cliente.idLegal";
export const FILTER_SEARCH_USUARIO_IDLEGAL = "operacion.usuario.idLegal";
export const PREFIX_QUERY_RECUPERADO = "impago.reclamacion.";
export const FECHA_RECUPERADO = "pago.fechaPago";
export const PREFIX_QUERY_IMPAGO = "reclamacion.";

export default {
  mixins: [
    DatesMixin,
    RoutesMixin,
    StringsMixin,
    CurrencyMixin,
    JavascriptMixin,
    AbilitiesMixin,
    ReclamacionMixin,
    ExportCSVMixin,
    FiltersMixin,
    ProductsMixin,
  ],
  props: [
    "operacionId",
    "divisa",
    "myClaims",
    "idLegalCliente",
    "idLegalUsuario",
  ],
  components: {
    Filters,
    SpecificFilters,
    ProgressSpinner,
    ButtonResetFilters,
  },
  data() {
    return {
      numPagina: 0,
      tamPagina: 10,
      sort: ["-fechaAlta"],
      loading: false,
      totalRecords: 0,
      filters: {},
      displayDialogExportarCSV: false,
      dataTableReclamacion: null,
      dataTableSelectedReclamacion: null,
      dataTableSelectedReclamacionBoton: null,
      displayMyOperationsAgentFilter: false,
      displayMyClaimsAgentFilter: false,
      displayDialogAsignarNotario: false,
      msgcontent: null,
      statuses: [],
      errorsFieldsNotary: [],
      fieldsToExcludeFromFilters: [],
      listadoAccionesPosibles: [],
      origenBotonera: ORIGEN_BOTONERA_RECLAMACIONES.RECLAMACION,
      switchSpecificFilters: 1,
      exportCSVSize: 50,
      descargandoCSV: false,
      products: [],
      phases: [
        { code: "01", name: this.$t("reclamacion.estadisticas.primeraFase") },
        { code: "02", name: this.$t("reclamacion.estadisticas.segundaFase") },
      ],
    };
  },
  created() {
    this.search();
    this.loading = true;
    this.displayMyClaimsAgentFilter = this.canFilterByMyClaims;
    this.FILTER_SEARCH_FECHA_ALTA = FILTER_SEARCH_FECHA_RECLAMACION;
    if (!this.isNotNotary()) {
      this.$set(
        this.filters,
        FILTER_SEARCH_NOTARIO_UUID,
        this.$store.state.profile.uuid
      );
    }
    if (this.idLegalCliente) {
      this.$set(
        this.filters,
        FILTER_SEARCH_CLIENTE_IDLEGAL,
        this.idLegalCliente
      );
    }
    /*
        if (this.idLegalUsuario) {
            this.$set(this.filters, FILTER_SEARCH_USUARIO_IDLEGAL , this.idLegalUsuario);
        }
        */
    this.fieldsToExcludeFromFilters = [
      FILTER_SEARCH_NOTARIO_UUID,
      FILTER_SEARCH_OPERACION_ID,
    ];
  },
  mounted() {
    this.loading = false;
  },
  computed: {
    backendFilters() {
      var filters2 = [];
      for (let [key, value] of Object.entries(this.filters)) {
        if (value != null && value != undefined && `${value}`) {
          if (
            [FILTER_SEARCH_NOTARIO_UUID, FILTER_SEARCH_AGENTE_UUID].includes(
              key
            )
          ) {
            if (value == IS_NULL) {
              filters2.push(`${key}~null=null`);
            } else {
              filters2.push(`${key}~uuid=${value}`);
            }
          } else if (key == FILTER_SEARCH_FECHA_RECLAMACION) {
            for (var i = 0; i < value.length; i++) {
              let filterDate = value[i];
              filters2.push(filterDate);
            }
          } else {
            filters2.push(`${key}~eq=${value}`);
          }
        }
      }
      return filters2;
    },
    canFilterByMyClaims() {
      return this.checkAbility("agente") && this.myClaims;
    },
    skipQueriesCalculosImportes() {
      if (this.operacionId) return false;
      return true;
    },
  },
  watch: {
    filters() {
      this.$store.commit("updateFiltrosReclamaciones", this.filters);
    },
  },
  methods: {
    esProductoNotarial(codigoProducto) {
      return codigoProducto == PRODUCTOS.PRODUCTO_ICIRED_NOTARIAL;
    },
    search() {
      this.loading = true;
      this.$apollo.queries.dataTableReclamacion.refetch();
    },
    reload() {
      if (this.operacionId) {
        this.$emit("reloadDetalleOperacion", true);
      }
      this.search();
    },
    onPage(event) {
      this.numPagina = event.page;
    },
    customSort(event) {
      var sortField = event.sortField;
      var sortOrder = event.sortOrder == -1 ? "-" : "";
      var sortToApply = sortOrder + sortField;
      var sorts = [sortToApply];
      this.sort = sorts;
      this.search();
    },
    gotoDetalle(token) {
      this.goToClaimView(token);
    },
    openAsignarNotarioOnClick(datos) {
      this.displayDialogAsignarNotario = true;
      this.dataTableSelectedReclamacionBoton = datos;
    },
    closeAsignarNotarioOnClick() {
      this.displayDialogAsignarNotario = false;
      this.dataTableSelectedReclamacionBoton = null;
    },
    removeFilter(value) {
      const fixedFiltersForNotary = [FILTER_SEARCH_NOTARIO_UUID];
      if (
        (this.isNotNotary() && fixedFiltersForNotary.includes(value)) ||
        !fixedFiltersForNotary.includes(value)
      ) {
        this.$set(this.filters, value, null);
      }
      this.switchSpecificFilters += 1;
    },
    filterBySpecificFilters(jsonValue) {
      if (jsonValue.type == "check") {
        if (jsonValue.value == true) {
          this.$set(
            this.filters,
            FILTER_SEARCH_AGENTE_UUID,
            this.$store.state.profile.uuid
          );
        } else {
          this.$set(this.filters, FILTER_SEARCH_AGENTE_UUID, null);
        }
      } else if (jsonValue.type == "date") {
        this.$set(
          this.filters,
          FILTER_SEARCH_FECHA_RECLAMACION,
          jsonValue.value
        );
      }
    },
    isNotNotary() {
      return !this.checkAbility("notario");
    },
    asignarNotario(tokenReclamacion) {
      this.$apollo
        .mutate({
          mutation: EN_NOTARIA_RECLAMACION,
          variables: {
            notarioUuid: this.$store.state.profile.uuid,
            token: tokenReclamacion,
          },
          update(data) {
            return data;
          },
        })
        .then(() => {
          this.errorsFieldsNotary = [];
          this.displayDialogAsignarNotario = false;
          this.search();
        })
        .catch((exception) => {
          this.errorsFieldsNotary = [];
          exception.graphQLErrors.forEach((error) => {
            this.errorsFieldsNotary.push(
              this.$i18n.t("acciones.errores." + error.message)
            );
          });
        });
    },
    addFilter(filter) {
      var generalFilters = [];
      generalFilters = generalFilters
        .concat(this.backendFilters)
        .concat(
          `operacion.producto.contrato.cliente.idLegal~eq=${this.idLegalCliente}`
        );
      if (filter != null) generalFilters.push(filter);
      if (this.operacionId != undefined && this.operacionId != null)
        generalFilters.push(
          `${FILTER_SEARCH_OPERACION_ID}~eq=${this.operacionId}`
        );
      return generalFilters;
    },
    downloadCSV() {
      this.$toast.add({
        severity: "info",
        summary: this.$t("common.mensajes.downloadingCSVTitle"),
        detail: this.$t("common.mensajes.downloadingCSV"),
        life: 3000,
      });
      this.descargandoCSV = true;
      this.displayDialogExportarCSV = true;
    },
    getReclamaciones() {
      this.$apollo
        .query({
          query: QUERY_LISTADO_RECLAMACION,
          variables: {
            filter: this.addFilter(null),
            sort: this.sort,
            page: 0,
            size: this.totalRecords,
            idioma: this.$i18n.locale,
          },
        })
        .then((data) => {
          this.displayDialogExportarCSV = false;
          this.download(
            this.getCSVData(
              data.data.listaReclamaciones.results,
              "reclamacion",
              ["__typename", "id", "listadoTransiciones", "estado"]
            ), // Estado se trae por literales
            "ListaReclamaciones"
          );
          this.descargandoCSV = false;
          this.loading = false;
        })
        .catch(() => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("common.error.downloadFailTitle"),
            detail:
              this.$t("common.error.downloadFail") + "ListaReclamaciones.csv",
            life: 5000,
          });
          this.descargandoCSV = false;
          this.loading = false;
        });
    },
    addFiltersRecuperado(columnDate) {
      var generalFilters = [];
      generalFilters = generalFilters.concat(
        this.backendFilters.map((f) => PREFIX_QUERY_RECUPERADO + f)
      );
      if (columnDate != null) {
        generalFilters = generalFilters.filter(
          (e) =>
            !e.startsWith(
              PREFIX_QUERY_RECUPERADO + FILTER_SEARCH_FECHA_RECLAMACION
            )
        );
        var start = new Date();
        var end = new Date(start.getTime());
        this.buildFilterBetween(columnDate, start, end).map((f) =>
          generalFilters.push(f)
        );
      }
      if (this.operacionId != undefined && this.operacionId != null)
        generalFilters.push(
          `${PREFIX_QUERY_RECUPERADO + FILTER_SEARCH_OPERACION_ID}~eq=${
            this.operacionId
          }`
        );
      return generalFilters;
    },
    addFiltersImpagos() {
      var generalFilters = [];
      generalFilters = generalFilters.concat(
        this.backendFilters.map((f) => PREFIX_QUERY_IMPAGO + f)
      );
      if (this.operacionId != undefined && this.operacionId != null)
        generalFilters.push(
          `${PREFIX_QUERY_IMPAGO + FILTER_SEARCH_OPERACION_ID}~eq=${
            this.operacionId
          }`
        );
      return generalFilters;
    },
    procesarFiltroHightlight(value) {
      this.$set(this.filters, "estado", value);
    },
    procesarFiltroHightFase(value) {
      this.$set(this.filters, "codFase", value);
    },
  },
  apollo: {
    dataTableReclamacion: {
      query: QUERY_LISTADO_RECLAMACION,
      variables() {
        return {
          filter: this.addFilter(null),
          sort: this.sort,
          page: this.numPagina,
          size: this.tamPagina,
          idioma: this.$i18n.locale,
        };
      },
      update(data) {
        this.totalRecords = data.listaReclamaciones.totalElements;
        this.loading = false;
        return data.listaReclamaciones.results;
      },
      error() {
        this.$toast.add({
          severity: "error",
          summary: this.$t("common.error.updateTitle"),
          detail: this.$t("common.error.update"),
          life: 5000,
        });
      },
    },
    statuses: {
      query: QUERY_LISTADOS_LITERALES_ESTADOS,
      variables() {
        return {
          idioma: this.$i18n.locale,
        };
      },
      update(data) {
        const estados = data.statuses.map((estado) => {
          return {
            name: estado.valor,
            code: estado.clave,
          };
        });
        return estados;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
</style>